import React, { useMemo, useState } from "react"
import { View, Text, TouchableOpacity, StyleSheet } from "react-native"
import moment from "moment"
import "moment/locale/fr"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { ModalSkeleton } from "@common/modals/ModalSkeleton"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { capitalize } from "../constants/summerContent"
import { formatEuros } from "../../brevet/utils/formatEuros"
import { summerMonthlyBreakdown } from "../utils/summerStartPricing"

/**
 * « Début des cours : Mardi 7 juillet » — cliquable dès que plusieurs options de
 * démarrage existent : démarrer plus tard = moins de séances ce mois-ci = 1ʳᵉ
 * facture moins chère.
 *
 * Le choix vit dans une MODALE, pas déplié sous la ligne : la liste couvre
 * désormais toutes les séances jusqu'à la fin de l'édition (fin août), soit
 * jusqu'à ~20 dates pour un panier 3 créneaux — un empilement de rangées
 * pleine largeur noierait la CTA de paiement. Même refonte, même squelette que
 * {@see StageSubjectPickerModal} (le « mur de chips » des matières de stage) :
 * on réutilise {@see ModalSkeleton}, aucune modale maison.
 *
 * Dedans, les dates sont groupées par MOIS puis par SEMAINE, une pastille par
 * jour — la décision du parent est d'abord « quelle semaine je rentre de
 * vacances », le jour ensuite. Une grille de pastilles tient ~20 dates en ~7
 * rangées. Le prix n'est donc plus répété sur chaque ligne : il s'affiche pour
 * la date en cours de sélection, juste au-dessus du bouton.
 *
 * Ce récap montre CE QU'ON PAIE PAR MOIS, pas un « 1ᵉʳ mois » (faux quand on
 * entre en août ou qu'on ne prend que juillet) : le mois d'entrée à son prorata,
 * puis — seulement s'il est choisi — le mois suivant au tarif plein (règle
 * partagée {@see summerMonthlyBreakdown} / {@see summerSecondFullMonth}, la même
 * que la 2ᵉ échéance du checkout).
 *
 * La modale édite une COPIE de travail (`staged`) ; rien n'est appliqué tant
 * que le parent n'a pas tapé « Valider » — le prorata de l'écran ne bouge donc
 * pas sous ses yeux pendant qu'il explore les dates.
 *
 * Dates et montants viennent de l'endpoint start-options — jamais recalculés ici.
 */

const dayOf = (option) => moment(option.first_session || option.start_date)

/** Mois → semaines ISO → options, en conservant l'ordre chronologique servi. */
const groupByMonthAndWeek = (options) => {
    const months = []
    options.forEach((option) => {
        const day = dayOf(option).locale("fr")
        const monthKey = day.format("YYYY-MM")
        const weekKey = day.format("GGGG-WW")

        let month = months[months.length - 1]
        if (!month || month.key !== monthKey) {
            month = {
                key: monthKey,
                label: capitalize(day.format("MMMM")),
                weeks: [],
            }
            months.push(month)
        }

        let week = month.weeks[month.weeks.length - 1]
        if (!week || week.key !== weekKey) {
            week = { key: weekKey, options: [] }
            month.weeks.push(week)
        }
        week.options.push(option)
    })

    return months
}

const SummerStartPicker = ({
    options,
    selectedOption,
    onSelect,
    monthlyPriceCents,
    chosenMonths,
    style,
}) => {
    const [open, setOpen] = useState(false)
    const [staged, setStaged] = useState(selectedOption)

    const months = useMemo(
        () => groupByMonthAndWeek(options || []),
        [options]
    )

    if (!options?.length || !selectedOption) return null

    const multiple = options.length > 1
    const dateLabel = capitalize(
        dayOf(selectedOption).locale("fr").format("dddd D MMMM")
    )

    // Ouvrir re-sème la copie de travail — la modale repart toujours de la date
    // réellement retenue. Le reste du corps de la modale (grille + récap) n'est
    // construit QUE lorsqu'elle est ouverte (cf. le garde `open && multiple`
    // ci-dessous) : cet écran re-render à chaque frappe et la modale est fermée
    // ~toujours, inutile d'allouer ~20 dates × formats à chaque fois.
    const stagedOption = staged || selectedOption
    const stagedBreakdown = summerMonthlyBreakdown(
        stagedOption,
        monthlyPriceCents,
        chosenMonths
    )

    return (
        <View style={style}>
            <TouchableOpacity
                disabled={!multiple}
                onPress={() => {
                    setStaged(selectedOption)
                    setOpen(true)
                }}
                style={styles.row}
                activeOpacity={0.7}
                accessibilityRole={multiple ? "button" : undefined}
            >
                <FontAwesome5
                    name="calendar-alt"
                    size={12}
                    color={theme.colors.grey}
                    style={styles.icon}
                />
                <Text style={styles.label}>
                    Début des cours :{" "}
                    <Text style={styles.value}>{dateLabel}</Text>
                </Text>
                {multiple ? <Text style={styles.edit}>Modifier</Text> : null}
            </TouchableOpacity>

            <ModalSkeleton
                isVisible={open && multiple}
                onClose={() => setOpen(false)}
                title="Quand commencer ?"
            >
                {open && multiple && (
                    <View>
                        {months.map((month) => (
                            <View key={month.key} style={styles.monthBlock}>
                                <Text style={styles.monthLabel}>{month.label}</Text>
                                {month.weeks.map((week) => (
                                    <View key={week.key} style={styles.weekRow}>
                                        {week.options.map((option) => {
                                            const isStaged =
                                                option.start_date ===
                                                stagedOption.start_date
                                            const day = dayOf(option).locale("fr")

                                            return (
                                                <TouchableOpacity
                                                    key={option.start_date}
                                                    style={[
                                                        styles.dayChip,
                                                        isStaged &&
                                                            styles.dayChipSelected,
                                                    ]}
                                                    activeOpacity={0.8}
                                                    accessibilityRole="button"
                                                    accessibilityState={{
                                                        selected: isStaged,
                                                    }}
                                                    accessibilityLabel={capitalize(
                                                        day.format("dddd D MMMM")
                                                    )}
                                                    onPress={() =>
                                                        setStaged(option)
                                                    }
                                                >
                                                    <Text
                                                        style={[
                                                            styles.dayChipText,
                                                            isStaged &&
                                                                styles.dayChipTextSelected,
                                                        ]}
                                                    >
                                                        {capitalize(
                                                            day
                                                                .format("ddd")
                                                                .replace(".", "")
                                                        )}{" "}
                                                        {day.format("D")}
                                                    </Text>
                                                </TouchableOpacity>
                                            )
                                        })}
                                    </View>
                                ))}
                            </View>
                        ))}

                        {/* Récap de la date en cours de sélection — le prix n'est
                            pas répété sur chaque pastille, il vit ici, par mois. */}
                        <View style={styles.recap}>
                            <Text style={styles.recapDate}>
                                {capitalize(
                                    dayOf(stagedOption)
                                        .locale("fr")
                                        .format("dddd D MMMM")
                                )}
                            </Text>
                            <Text style={styles.recapPrice}>
                                {stagedBreakdown
                                    .map(
                                        (l) =>
                                            `${l.label} ${formatEuros(l.cents / 100)}`
                                    )
                                    .join(" · ")}
                            </Text>
                        </View>

                        <FilledButtonM
                            label="Valider cette date"
                            onPress={() => {
                                onSelect(stagedOption)
                                setOpen(false)
                            }}
                            color={theme.colors.primary}
                            style={styles.validateButton}
                        />
                    </View>
                )}
            </ModalSkeleton>
        </View>
    )
}

const styles = StyleSheet.create({
    row: { flexDirection: "row", alignItems: "center", paddingVertical: 3 },
    icon: { width: 20 },
    label: {
        flex: 1,
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        lineHeight: 16,
    },
    value: {
        fontFamily: "Montserrat-Bold",
        color: theme.colors.primary,
    },
    edit: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
        textDecorationLine: "underline",
        marginLeft: 8,
    },
    monthBlock: { marginBottom: 14 },
    monthLabel: {
        fontFamily: "Montserrat-Bold",
        fontSize: 11,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: theme.colors.primary,
        marginBottom: 8,
    },
    weekRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginBottom: 6,
    },
    dayChip: {
        borderWidth: 1.5,
        borderColor: theme.colors.lightMore,
        borderRadius: 8,
        paddingVertical: 7,
        paddingHorizontal: 10,
        backgroundColor: theme.colors.white,
    },
    dayChipSelected: {
        borderColor: theme.colors.primary,
        backgroundColor: theme.colors.primaryLighter,
    },
    dayChipText: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 12,
        color: theme.colors.grey,
    },
    dayChipTextSelected: { color: theme.colors.primary },
    recap: {
        gap: 3,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        paddingTop: 12,
        marginTop: 2,
    },
    recapDate: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.primary,
    },
    recapPrice: {
        fontFamily: "Montserrat-SemiBold",
        fontSize: 13,
        color: theme.colors.grey,
    },
    validateButton: { width: "100%", marginTop: 16, marginBottom: 0 },
})

export default SummerStartPicker
